import { useCallback, useEffect, useMemo, useState } from 'react';

const queryCache = new Map();
const inflight = new Map();
const listeners = new Map();

const serializeKey = (key) => {
  if (Array.isArray(key)) return JSON.stringify(key);
  return String(key);
};

const getCacheEntry = (cacheKey) => queryCache.get(cacheKey);

const subscribe = (cacheKey, listener) => {
  if (!listeners.has(cacheKey)) listeners.set(cacheKey, new Set());
  listeners.get(cacheKey).add(listener);
  return () => {
    const set = listeners.get(cacheKey);
    if (!set) return;
    set.delete(listener);
    if (set.size === 0) listeners.delete(cacheKey);
  };
};

const notify = (cacheKey) => {
  const set = listeners.get(cacheKey);
  if (!set) return;
  set.forEach((listener) => listener());
};

const isStale = (entry, staleTime) => {
  if (!entry) return true;
  return (Date.now() - entry.updatedAt) > staleTime;
};

// ============================================
// GLOBAL EVENT BUS — sayfalar arası invalidation
// ============================================
const eventBus = new EventTarget();

/**
 * Bir mutasyon sonrası ilgili cache domain'lerini invalidate et.
 * Kullanım: invalidateQueries('stok') veya invalidateQueries(['stok', 'dashboard'])
 */
export const invalidateQueries = (domains) => {
  const domainList = Array.isArray(domains) ? domains : [domains];
  domainList.forEach(domain => {
    // Cache'deki eşleşen key'leri stale yap
    for (const [cacheKey, entry] of queryCache.entries()) {
      if (cacheKey.includes(domain)) {
        entry.updatedAt = 0; // force stale
        notify(cacheKey);
      }
    }
    // Global event — henüz mount olmamış sayfalar için
    eventBus.dispatchEvent(new CustomEvent('invalidate', { detail: domain }));
  });
};

/**
 * Mutasyon helper — POST/PUT/PATCH/DELETE sonrası çağır.
 * Kullanım: await apiMutate('/api/recipes', { method: 'POST', body: ... }, ['recete', 'dashboard'])
 */
export const apiMutate = async (url, options = {}, invalidateDomains = []) => {
  const token = localStorage.getItem('access_token');
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(options.body);
  }

  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || err.message || 'İşlem başarısız');
  }
  const data = await res.json().catch(() => ({}));

  // Mutasyon başarılıysa ilgili cache'leri invalidate et
  if (invalidateDomains.length > 0) {
    invalidateQueries(invalidateDomains);
  }

  return data;
};

export const setQueryData = (key, updater) => {
  const cacheKey = serializeKey(key);
  const previousData = getCacheEntry(cacheKey)?.data;
  const nextData = typeof updater === 'function' ? updater(previousData) : updater;
  queryCache.set(cacheKey, { data: nextData, error: null, updatedAt: Date.now() });
  notify(cacheKey);
  return previousData;
};

export const useCachedQuery = (key, fetcher, options = {}) => {
  const {
    staleTime = 15000,
    revalidateOnMount = true,
    revalidateOnFocus = true,
    refetchInterval = 0,
    fallbackData
  } = options;
  const hasFallbackData = Object.prototype.hasOwnProperty.call(options, 'fallbackData');

  const cacheKey = useMemo(() => serializeKey(key), [key]);

  const [data, setData] = useState(() => {
    const entry = getCacheEntry(cacheKey);
    if (entry) return entry.data;
    if (hasFallbackData) return fallbackData;
    return undefined;
  });
  const [error, setError] = useState(() => getCacheEntry(cacheKey)?.error || null);
  const [isLoading, setIsLoading] = useState(() => {
    const entry = getCacheEntry(cacheKey);
    return !entry && !hasFallbackData;
  });
  const [isValidating, setIsValidating] = useState(false);

  const syncFromCache = useCallback(() => {
    const entry = getCacheEntry(cacheKey);
    if (entry) {
      setData(entry.data);
      setError(entry.error || null);
      setIsLoading(false);
      return;
    }
    if (hasFallbackData) {
      setData(fallbackData);
      setError(null);
      setIsLoading(false);
      return;
    }
    setData(undefined);
    setError(null);
    setIsLoading(true);
  }, [cacheKey, hasFallbackData, fallbackData]);

  const revalidate = useCallback(async ({ force = false } = {}) => {
    const entry = getCacheEntry(cacheKey);
    if (!force && entry && !isStale(entry, staleTime)) {
      return entry.data;
    }

    if (inflight.has(cacheKey)) {
      return inflight.get(cacheKey);
    }

    setIsValidating(true);
    if (data === undefined) setIsLoading(true);

    const request = Promise.resolve(fetcher())
      .then((result) => {
        queryCache.set(cacheKey, {
          data: result,
          error: null,
          updatedAt: Date.now()
        });
        notify(cacheKey);
        return result;
      })
      .catch((err) => {
        const previous = getCacheEntry(cacheKey);
        queryCache.set(cacheKey, {
          data: previous ? previous.data : undefined,
          error: err,
          updatedAt: Date.now()
        });
        notify(cacheKey);
        throw err;
      })
      .finally(() => {
        inflight.delete(cacheKey);
        setIsLoading(false);
        setIsValidating(false);
      });

    inflight.set(cacheKey, request);
    return request;
  }, [cacheKey, data, fetcher, staleTime]);

  const mutate = useCallback((updater, mutateOptions = {}) => {
    const previousData = setQueryData(cacheKey, updater);
    if (mutateOptions.revalidate) revalidate({ force: true });
    return previousData;
  }, [cacheKey, revalidate]);

  useEffect(() => subscribe(cacheKey, syncFromCache), [cacheKey, syncFromCache]);

  // İlk mount + stale kontrol
  useEffect(() => {
    syncFromCache();
    const entry = getCacheEntry(cacheKey);
    const shouldRevalidate = revalidateOnMount && isStale(entry, staleTime);
    if (shouldRevalidate) {
      revalidate({ force: true }).catch(() => {});
    }
  }, [cacheKey, revalidate, revalidateOnMount, staleTime, syncFromCache]);

  // Window focus → stale ise yenile (sekmeye dönünce)
  useEffect(() => {
    if (!revalidateOnFocus) return;
    const onFocus = () => {
      const entry = getCacheEntry(cacheKey);
      if (isStale(entry, staleTime)) {
        revalidate({ force: true }).catch(() => {});
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [cacheKey, revalidate, revalidateOnFocus, staleTime]);

  // Opsiyonel otomatik yenileme aralığı
  useEffect(() => {
    if (!refetchInterval || refetchInterval <= 0) return;
    const interval = setInterval(() => {
      revalidate({ force: true }).catch(() => {});
    }, refetchInterval);
    return () => clearInterval(interval);
  }, [refetchInterval, revalidate]);

  // Global invalidation dinle
  useEffect(() => {
    const handler = () => {
      revalidate({ force: true }).catch(() => {});
    };
    eventBus.addEventListener('invalidate', handler);
    return () => eventBus.removeEventListener('invalidate', handler);
  }, [revalidate]);

  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
    revalidate
  };
};

export default useCachedQuery;
